'use client';

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// TinyMCE React wrapper - assets loaded from CDN (media.tracverse.com)
import { Editor } from '@tinymce/tinymce-react';

// TinyMCE CDN base URL (self-hosted on our S3/CloudFront)
const TINYMCE_CDN_URL = 'https://media.tracverse.com/shared/tinymce';
import DOMPurify from 'dompurify';
import { Navbar } from '../../components/layout/navbar';
import { Footer } from '../../components/layout/footer';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui/form';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '../../components/ui/avatar';
import { toast } from 'sonner';
import { useTopics } from '../../hooks/use-topics';
import {
  ImageIcon,
  X,
  Tag,
  Calendar,
  Clock,
  Upload,
  FileText,
  Eye,
  Check,
  Atom,
  Sparkles,
  Zap,
  Orbit,
  Layers,
} from 'lucide-react';
import axios from 'axios';
import { useAuthStore } from '../../stores';
import { useUploadCoverImage } from '../../hooks/use-upload';
import { useCreateArticle } from '../../hooks/use-articles';
import { ProcessingSpinner } from '../../components/feedback/processing-spinner';
import { useGetUserById, useCheckUserLogin } from '../../hooks/use-user';

// Form validation schema
const formSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  subtitle: z.string().optional(),
  content: z.string().min(1, 'Content is required'),
  topics: z
    .array(z.string())
    .min(1, 'At least one topic is required')
    .max(3, 'Maximum 3 topics allowed'),
});

export default function CreateBlogPage() {
  const navigate = useNavigate();

  const { walletAddress, isAuthenticated } = useAuthStore();
  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
  } = useGetUserById(walletAddress);
  const { data: isLoggedIn, isLoading, isError } = useCheckUserLogin();

  const { data: availableTopics = [] } = useTopics();

  const [coverImage, setCoverImage] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState('');
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [estimatedReadTime, setEstimatedReadTime] = useState('3 min read');
  const [spinnerStatus, setSpinnerStatus] = useState('processing'); // 'processing' | 'success' | 'error'
  const [spinnerError, setSpinnerError] = useState(null);

  const fileInputRef = useRef(null);
  const titleInputRef = useRef(null);
  const editorRef = useRef(null);

  // Initialize form with react-hook-form and zod validation
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      subtitle: '',
      content: '',
      topics: [],
    },
  });

  // Get current form values for preview
  const { title, subtitle, content, topics: selectedTopics } = form.watch();

  // Focus on title input when component mounts
  useEffect(() => {
    if (titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, []);

  // Calculate estimated read time
  useEffect(() => {
    if (content) {
      // Strip HTML tags for word count calculation
      const textContent = content.replace(/<[^>]*>/g, '');
      const words = textContent.trim().split(/\s+/).length;
      const minutes = Math.max(1, Math.ceil(words / 200));
      setEstimatedReadTime(`${minutes} min read`);
    }
  }, [content]);

  // Sync TinyMCE content with form state when switching to edit mode
  useEffect(() => {
    if (!previewMode && editorRef.current && typeof content === 'string') {
      // Small delay to ensure TinyMCE is fully ready after re-render
      const timeoutId = setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.setContent(content || '');
          // Force a repaint/refresh of the editor
          editorRef.current.focus();
          editorRef.current.selection.select(editorRef.current.getBody(), true);
          editorRef.current.selection.collapse(false);
        }
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [previewMode, content]);

  // Use the upload hook
  const uploadCoverMutation = useUploadCoverImage(
    (progress) => {
      setIsUploadingCover(true);
      // Optionally, you can set a progress state here if you want a progress bar
    },
    {
      onSuccess: (data) => {
        setIsUploadingCover(false);
        if (data && data.url) {
          setCoverImagePreview(data.url);
          setCoverImage(data.url);
          toast.success('Cover image uploaded!', {
            description: 'Your cover image was uploaded successfully.',
          });
        } else {
          toast.error('Cover image upload failed', {
            description: 'No url returned from server.',
          });
        }
      },
      onError: (error) => {
        setIsUploadingCover(false);
        toast.error('Cover image upload failed', {
          description: error?.message || 'An error occurred during upload.',
        });
      },
    }
  );

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Show preview immediately (optional, can be removed if you want to only show after upload)
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Upload image
      const formData = new FormData();
      formData.append('file', file);
      uploadCoverMutation.mutate(formData);
    }
  };

  const handleTopicToggle = (topic) => {
    const currentTopics = form.getValues('topics');

    if (currentTopics.includes(topic)) {
      form.setValue(
        'topics',
        currentTopics.filter((t) => t !== topic),
        { shouldValidate: true }
      );
    } else {
      if (currentTopics.length < 3) {
        form.setValue('topics', [...currentTopics, topic], {
          shouldValidate: true,
        });
      } else {
        toast.error('Topic limit reached', {
          description: 'You can select up to 3 topics',
        });
      }
    }
  };

  const handleEditorChange = (content) => {
    form.setValue('content', content, { shouldValidate: true });
  };

  const createArticle = useCreateArticle();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSpinnerStatus('processing');
    setSpinnerError(null);
    try {
      const particle = {
        ...data,
        htmlContent: data.content,
        creator: walletAddress,
        coverImage,
      };
      const result = await createArticle.mutateAsync(particle);
      toast.success('Particle published!', {
        description: result.mintSuccess
          ? 'Your particle has been published and minted successfully!'
          : 'Your particle has been published, but minting may have failed.',
      });
      setSpinnerStatus('success');
      // Optionally navigate after success
      navigate('/blog');
    } catch (error) {
      console.error('Error publishing particle:', error);
      setSpinnerStatus('error');
      setSpinnerError(
        error?.message ||
          'There was an error publishing your particle. Please try again.'
      );
    }
  };

  // If user is not logged in, redirect to sign in
  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center py-20">
            <div className="relative mb-8">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-cyan-500/20 rounded-full blur-2xl"></div>
              <div className="relative w-24 h-24 bg-zinc-900 rounded-full flex items-center justify-center mx-auto border border-white/10">
                <Atom
                  className="h-12 w-12 text-purple-400 animate-spin"
                  style={{ animationDuration: '3s' }}
                />
              </div>
            </div>
            <h1 className="text-3xl mb-4 bg-gradient-to-r from-white to-zinc-400 bg-clip-text text-transparent font-bold">
              Sign in to create particles
            </h1>
            <p className="text-zinc-400 mb-8 leading-relaxed">
              Join the knowledge universe to start creating and sharing your
              particles of wisdom.
            </p>
            <Button
              onClick={() => navigate('/signin')}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-2xl shadow-lg shadow-purple-500/20 transition-all hover:scale-105"
            >
              Sign In
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-violet-500/30">
      <ProcessingSpinner
        isOpen={isSubmitting}
        status={spinnerStatus}
        error={spinnerError}
        onClose={() => {
          setIsSubmitting(false);
          setSpinnerStatus('processing');
          setSpinnerError(null);
        }}
        onRetry={() => {
          setSpinnerStatus('processing');
          setSpinnerError(null);
          form.handleSubmit(onSubmit)();
        }}
        title="Publishing Particle"
        processingMessage="Your particle is being published. Please wait..."
        successMessage="Your particle has been published!"
        errorMessage="There was an error publishing your particle."
      />

      {/* Gradient Background - matching landing page */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
      </div>

      <Navbar />

      <main className="relative z-10 container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          {/* Enhanced Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-4 mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-violet-500 to-cyan-500 rounded-3xl blur-lg opacity-30 animate-pulse"></div>
                <div className="relative p-4 bg-gradient-to-r from-violet-600 to-cyan-600 rounded-3xl shadow-md">
                  <Sparkles className="h-8 w-8 text-white animate-pulse" />
                </div>
              </div>
              <div className="text-left">
                <h1 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-white via-violet-200 to-zinc-400 bg-clip-text text-transparent">
                  Create
                </h1>
                <h1 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
                  Particle
                </h1>
              </div>
            </div>

            <p className="text-xl text-zinc-400 mb-10 max-w-3xl mx-auto leading-relaxed">
              Transform your ideas into knowledge particles that will inspire
              and educate the universe
            </p>

            {/* Mode Toggle */}
            <div className="relative group max-w-md mx-auto">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-500/20 to-cyan-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
              <div className="relative bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-2 shadow-sm">
                <div className="flex items-center">
                  <Button
                    variant={previewMode ? 'outline' : 'default'}
                    onClick={() => setPreviewMode(false)}
                    className={`flex-1 gap-2 rounded-xl border-0 ${
                      !previewMode
                        ? 'bg-gradient-to-r from-violet-600 to-cyan-600 text-white shadow-sm'
                        : 'hover:bg-white/5 text-zinc-400 hover:text-white bg-transparent'
                    }`}
                  >
                    <FileText className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant={previewMode ? 'default' : 'outline'}
                    onClick={() => setPreviewMode(true)}
                    className={`flex-1 gap-2 rounded-xl ml-2 border-0 ${
                      previewMode
                        ? 'bg-gradient-to-r from-violet-600 to-cyan-600 text-white shadow-sm'
                        : 'hover:bg-white/5 text-zinc-400 hover:text-white bg-transparent'
                    }`}
                  >
                    <Eye className="h-4 w-4" />
                    Preview
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {previewMode ? (
            // Enhanced Preview Mode
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-500/10 to-cyan-500/10 rounded-3xl blur-xl"></div>
              <div className="relative bg-black/40 backdrop-blur-md border border-white/10 rounded-3xl overflow-hidden shadow-sm">
                {coverImagePreview && (
                  <div className="relative h-[400px] overflow-hidden">
                    <img
                      src={coverImagePreview || '/placeholder.svg'}
                      alt="Cover"
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  </div>
                )}

                <div className="p-8 md:p-12">
                  <div className="flex flex-wrap gap-2 mb-6">
                    {selectedTopics.map((topic) => (
                      <Badge
                        key={topic}
                        className="bg-white/10 text-zinc-300 border-white/10 px-3 py-1 hover:bg-white/20"
                      >
                        {topic}
                      </Badge>
                    ))}
                  </div>

                  <h1 className="text-4xl md:text-5xl font-black mb-6 text-white leading-tight">
                    {title || 'Untitled Particle'}
                  </h1>

                  <p className="text-xl text-zinc-400 mb-8 leading-relaxed">
                    {subtitle || 'No subtitle provided'}
                  </p>

                  <div className="flex items-center gap-4 mb-8 pb-8 border-b border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-violet-500/20 to-cyan-500/20 rounded-full blur-lg"></div>
                        <Avatar className="relative h-12 w-12 border border-white/10 shadow-sm">
                          <AvatarImage
                            src={user?.avatar || '/placeholder.svg'}
                            alt={user?.name}
                          />
                          <AvatarFallback className="bg-zinc-800 text-zinc-400">
                            {user?.name?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {user?.name || 'Anonymous'}
                        </p>
                        <div className="flex items-center gap-3 text-sm text-zinc-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>{new Date().toLocaleDateString()}</span>
                          </div>
                          <span>•</span>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>{estimatedReadTime}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="prose prose-lg prose-invert max-w-none text-zinc-300 prose-headings:text-white prose-a:text-violet-400 prose-a:no-underline hover:prose-a:underline prose-strong:text-white prose-code:text-violet-300 prose-code:bg-violet-900/20 prose-code:px-1 prose-code:rounded prose-pre:bg-zinc-900/50 prose-pre:border prose-pre:border-white/10">
                    {content ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(content),
                        }}
                      />
                    ) : (
                      <div className="text-center py-12">
                        <div className="relative mb-6">
                          <div className="absolute inset-0 bg-gradient-to-r from-violet-500/20 to-cyan-500/20 rounded-full blur-xl"></div>
                          <div className="relative w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto border border-white/10">
                            <Atom className="h-8 w-8 text-zinc-500" />
                          </div>
                        </div>
                        <p className="text-zinc-500">
                          No content yet. Switch to edit mode to start creating
                          your particle.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Enhanced Edit Mode
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-8"
              >
                {/* Enhanced Cover Image Section */}
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-violet-500/10 to-cyan-500/10 rounded-3xl blur-xl"></div>
                  <div className="relative bg-black/40 backdrop-blur-md border border-white/10 rounded-3xl p-8 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-gradient-to-r from-violet-600 to-cyan-600 rounded-xl">
                        <ImageIcon className="h-5 w-5 text-white" />
                      </div>
                      <Label
                        htmlFor="coverImage"
                        className="text-xl text-white font-semibold"
                      >
                        Particle Cover
                      </Label>
                    </div>

                    {coverImagePreview ? (
                      <div className="relative rounded-2xl overflow-hidden h-[300px] mb-4 group/image border border-white/10">
                        <img
                          src={coverImagePreview || '/placeholder.svg'}
                          alt="Cover"
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                        {isUploadingCover && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-10">
                            <div className="flex items-center gap-3 text-white">
                              <Orbit className="h-6 w-6 animate-spin" />
                              <span className=" ">Uploading...</span>
                            </div>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/image:opacity-100 transition-opacity flex items-center justify-center gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-white border-white/20 bg-black/50 hover:bg-white/20 hover:text-white backdrop-blur-sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploadingCover}
                          >
                            Change cover
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="text-white border-white/20 bg-black/50 hover:bg-white/20 hover:text-white backdrop-blur-sm"
                            onClick={() => {
                              setCoverImage(null);
                              setCoverImagePreview('');
                            }}
                            disabled={isUploadingCover}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="relative group/upload border-2 border-dashed border-white/10 rounded-2xl p-12 text-center cursor-pointer hover:border-violet-500/50 transition-all bg-black/20"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-violet-500/5 to-cyan-500/5 rounded-2xl opacity-0 group-hover/upload:opacity-100 transition-opacity"></div>
                        <div className="relative">
                          <div className="mx-auto w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4 border border-white/5">
                            <ImageIcon className="h-8 w-8 text-violet-400" />
                          </div>
                          <h3 className="text-xl mb-2 text-white font-medium">
                            Add a cover image
                          </h3>
                          <p className="text-zinc-500 mb-4">
                            Recommended: 1600 x 840px
                          </p>
                          <Button
                            type="button"
                            className="bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-700 hover:to-cyan-700 text-white shadow-sm hover:shadow-md transition-all hover:scale-105"
                            size="sm"
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload image
                          </Button>
                        </div>
                      </div>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleCoverImageChange}
                      accept="image/*"
                      className="hidden"
                    />
                  </div>
                </div>

                {/* Enhanced Title and Subtitle Section */}
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-3xl blur-xl"></div>
                  <div className="relative bg-black/40 backdrop-blur-md border border-white/10 rounded-3xl p-8 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl">
                        <Zap className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-xl text-white font-semibold">
                        Particle Identity
                      </h3>
                    </div>

                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem className="mb-6">
                          <FormLabel className="text-zinc-300">Title</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              ref={titleInputRef}
                              placeholder="Enter a captivating title for your particle"
                              className="border-white/10 rounded-xl py-4 px-6 bg-black/50 backdrop-blur-sm text-white placeholder:text-zinc-600 focus:border-cyan-500"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="subtitle"
                      render={({ field }) => (
                        <FormItem className="mb-6">
                          <FormLabel className="text-zinc-300">
                            Subtitle{' '}
                            <span className="text-zinc-500 text-sm font-normal">
                              (optional)
                            </span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="A brief summary that captures the essence"
                              className="border-white/10 rounded-xl py-3 px-6 bg-black/50 backdrop-blur-sm text-white placeholder:text-zinc-600 focus:border-cyan-500"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-zinc-300">
                            Description{' '}
                            <span className="text-zinc-500 text-sm font-normal">
                              (optional, shown in previews)
                            </span>
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder="A short description for previews and discovery"
                              className="border-white/10 rounded-xl py-3 px-6 bg-black/50 backdrop-blur-sm min-h-[100px] text-white placeholder:text-zinc-600 focus:border-cyan-500"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Enhanced Topics Section */}
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-3xl blur-xl"></div>
                  <div className="relative bg-black/40 backdrop-blur-md border border-white/10 rounded-3xl p-8 shadow-sm">
                    <FormField
                      control={form.control}
                      name="topics"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl">
                                <Tag className="h-5 w-5 text-white" />
                              </div>
                              <FormLabel className="text-xl text-white font-semibold">
                                Particle Topics
                              </FormLabel>
                            </div>
                            <Badge
                              variant={
                                field.value.length > 0 ? 'default' : 'outline'
                              }
                              className={`px-3 py-1 ${
                                field.value.length > 0
                                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white border-0'
                                  : 'border-white/20 text-zinc-400'
                              }`}
                            >
                              {field.value.length}/3 selected
                            </Badge>
                          </div>

                          <FormControl>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
                              {availableTopics.map((topic) => (
                                <Badge
                                  key={topic.id}
                                  variant={
                                    field.value.includes(topic.name)
                                      ? 'default'
                                      : 'outline'
                                  }
                                  className={`text-sm py-3 px-4 cursor-pointer transition-all hover:scale-105 rounded-xl justify-center ${
                                    field.value.includes(topic.name)
                                      ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-sm border-0'
                                      : 'bg-black/30 border-white/10 text-zinc-400 hover:bg-white/10 hover:text-white'
                                  }`}
                                  onClick={() => handleTopicToggle(topic.name)}
                                >
                                  {field.value.includes(topic.name) && (
                                    <Check className="h-3 w-3 mr-2" />
                                  )}
                                  {topic.name}
                                </Badge>
                              ))}
                            </div>
                          </FormControl>

                          <FormDescription className="text-zinc-400 bg-black/30 p-4 rounded-xl border border-white/5">
                            Topics help readers discover your particle in the
                            knowledge universe. Select 1-3 topics that best
                            describe your content.
                          </FormDescription>

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Enhanced Content Editor Section */}
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-fuchsia-500/10 to-violet-500/10 rounded-3xl blur-xl"></div>
                  <div className="relative bg-black/40 backdrop-blur-md border border-white/10 rounded-3xl p-8 shadow-sm">
                    <FormField
                      control={form.control}
                      name="content"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-gradient-to-r from-fuchsia-600 to-violet-600 rounded-xl">
                                <Layers className="h-5 w-5 text-white" />
                              </div>
                              <FormLabel className="text-xl text-white font-semibold">
                                Particle Content
                              </FormLabel>
                            </div>
                            <div className="flex items-center gap-2 bg-fuchsia-900/20 border border-fuchsia-500/30 px-3 py-1 rounded-full">
                              <Clock className="h-4 w-4 text-fuchsia-400" />
                              <span className="text-sm font-medium text-fuchsia-400">
                                {estimatedReadTime}
                              </span>
                            </div>
                          </div>

                          <FormControl>
                            <div className="rounded-2xl overflow-hidden border border-white/10 focus-within:border-fuchsia-500/50 transition-colors">
                              <Editor
                                tinymceScriptSrc={`${TINYMCE_CDN_URL}/tinymce.min.js`}
                                key="editor-instance"
                                onInit={(evt, editor) =>
                                  (editorRef.current = editor)
                                }
                                initialValue=""
                                onEditorChange={handleEditorChange}
                                init={{
                                  license_key: 'gpl', // Required for TinyMCE 7+ open source usage
                                  iframe_aria_text: 'Particle Content Editor',
                                  resize_img_proportional: true,
                                  block_unsupported_drop: false,
                                  // CDN-hosted TinyMCE paths (media.tracverse.com)
                                  skin_url: `${TINYMCE_CDN_URL}/skins/ui/oxide-dark`,
                                  content_css: `${TINYMCE_CDN_URL}/skins/content/dark/content.min.css`,
                                  file_picker_types: 'image',
                                  automatic_uploads: true,
                                  images_upload_url: `${
                                    import.meta.env.VITE_API_BASE_URL
                                  }/omniverse/upload/particle-image`,
                                  images_upload_handler: async (
                                    blobInfo,
                                    progress
                                  ) => {
                                    try {
                                      const file = blobInfo.blob();
                                      if (file.size > 2 * 1024 * 1024) {
                                        throw new Error(
                                          'Image size should not exceed 2MB'
                                        );
                                      }
                                      const formData = new FormData();
                                      formData.append(
                                        'file',
                                        file,
                                        blobInfo.filename()
                                      );

                                      const response = await axios.post(
                                        `${
                                          import.meta.env.VITE_API_BASE_URL
                                        }/omniverse/upload/particle-image`,
                                        formData,
                                        {
                                          headers: {
                                            'Content-Type':
                                              'multipart/form-data',
                                          },
                                          onUploadProgress: (progressEvent) => {
                                            progress(
                                              (progressEvent.loaded /
                                                progressEvent.total) *
                                                100
                                            );
                                          },
                                        }
                                      );

                                      if (
                                        response.data.success &&
                                        response.data.url
                                      ) {
                                        return response.data.url;
                                      } else {
                                        throw new Error('Upload failed');
                                      }
                                    } catch (error) {
                                      if (editorRef.current) {
                                        editorRef.current.notificationManager.open(
                                          {
                                            text:
                                              error.message ||
                                              'Image upload failed',
                                            type: 'error',
                                            timeout: 3000,
                                          }
                                        );
                                      }
                                      throw error;
                                    }
                                  },
                                  file_picker_callback: (cb, value, meta) => {
                                    const input =
                                      document.createElement('input');
                                    input.setAttribute('type', 'file');
                                    input.setAttribute('accept', 'image/*');
                                    input.addEventListener('change', (e) => {
                                      const file = e.target.files[0];
                                      if (file.size > 2 * 1024 * 1024) {
                                        if (editorRef.current) {
                                          editorRef.current.notificationManager.open(
                                            {
                                              text: 'Image size should not exceed 2MB',
                                              type: 'error',
                                              timeout: 3000,
                                            }
                                          );
                                        }
                                        return;
                                      }
                                      const reader = new FileReader();
                                      reader.addEventListener('load', () => {
                                        const id =
                                          'blobid' + new Date().getTime();
                                        const blobCache =
                                          window.tinymce.activeEditor
                                            .editorUpload.blobCache;
                                        const base64 =
                                          reader.result.split(',')[1];
                                        const blobInfo = blobCache.create(
                                          id,
                                          file,
                                          base64
                                        );
                                        blobCache.add(blobInfo);
                                        cb(blobInfo.blobUri(), {
                                          title: file.name,
                                        });
                                      });
                                      reader.readAsDataURL(file);
                                    });
                                    input.click();
                                  },
                                  plugins: [
                                    'advlist',
                                    'autolink',
                                    'lists',
                                    'link',
                                    'image',
                                    'charmap',
                                    'preview',
                                    'anchor',
                                    'searchreplace',
                                    'visualblocks',
                                    'code',
                                    'fullscreen',
                                    'insertdatetime',
                                    'media',
                                    'table',
                                    'help',
                                    'wordcount',
                                  ],
                                  toolbar:
                                    'undo redo | blocks | ' +
                                    'bold italic backcolor | alignleft aligncenter ' +
                                    'alignright alignjustify | bullist numlist outdent indent | ' +
                                    'removeformat | help',
                                  content_style:
                                    'body { font-family:Helvetica,Arial,sans-serif; font-size:16px; background-color: #000; color: #fff; } img { max-width: 100%; height: auto; }',
                                }}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex justify-end gap-4 sticky bottom-4 bg-black/80 backdrop-blur-md p-4 rounded-xl shadow-lg border border-white/10 z-10">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(-1)}
                    className="border-white/10 text-zinc-300 hover:text-white hover:bg-white/5 bg-transparent rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="min-w-[150px] bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-700 hover:to-cyan-700 text-white shadow-lg shadow-violet-500/20 border-0 rounded-xl"
                  >
                    {isSubmitting ? 'Publishing...' : 'Publish Particle'}
                  </Button>
                </div>
              </form>
            </Form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
